/**
 * Pebble renderer. Loads /assets/lowpoly-rocks.glb and uses the smallest
 * meshes from that pack as pebble variants — so pebbles look like real
 * tiny rocks instead of generic spheres. Per-variant InstancedMesh keeps
 * the draw cost low even at 2000 pebbles.
 *
 * Held pebbles ride on their carrier ant's mouth; ground pebbles snap to
 * the terrain surface. The world's `pebble.color` is applied as a tint
 * over a flat material — we don't bind the pack's textures, so the
 * colour picker actually controls what the player sees.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Terrain } from '../game/Terrain';
import type { Pebbles } from '../sim/Pebbles';
import type { AntState } from '../sim/contracts';

// Target visible size for a pebble — the largest dimension of the rock
// is normalised to roughly this so they read as small debris regardless
// of the variant's native scale.
const PEBBLE_VISUAL_SIZE = 0.09;
const PACK_URL = '/assets/lowpoly-rocks.glb';
const NUM_VARIANTS = 5;

interface VariantSlot {
  mesh: THREE.InstancedMesh;
  /** Indices of pebbles assigned to this variant — pre-bucketed at load
   *  so update() can iterate this slot's pebbles directly with no
   *  per-frame variant lookup. */
  pebbleIdx: number[];
}

export class PebbleMesh {
  readonly group = new THREE.Group();
  private readonly dummy = new THREE.Object3D();
  private slots: VariantSlot[] = [];
  private loaded = false;

  constructor(private pebbles: Pebbles, private terrain: Terrain) {
    this.group.name = 'pebbles';
    void this.load();
  }

  private async load(): Promise<void> {
    let gltf;
    try {
      gltf = await new GLTFLoader().loadAsync(PACK_URL);
    } catch (err) {
      console.warn('[PebbleMesh] failed to load rock pack, using fallback:', err);
      this.fallbackSpheres();
      return;
    }
    gltf.scene.updateMatrixWorld(true);

    type RawVariant = {
      geo: THREE.BufferGeometry;
      mat: THREE.Material;     // pack's own material — keeps textures
      size: number;
    };
    const raw: RawVariant[] = [];
    gltf.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const geo = m.geometry.clone();
      geo.applyMatrix4(m.matrixWorld);
      geo.computeBoundingBox();
      const sz = new THREE.Vector3();
      geo.boundingBox!.getSize(sz);
      const len = sz.length();
      if (len < 1e-3) return;
      const mat = Array.isArray(m.material) ? m.material[0] : m.material;
      raw.push({ geo, mat, size: len });
    });
    if (raw.length === 0) {
      this.fallbackSpheres();
      return;
    }

    // Smallest N variants — read as pebbles, not boulders.
    raw.sort((a, b) => a.size - b.size);
    const chosen = raw.slice(0, Math.min(NUM_VARIANTS, raw.length));

    // Recenter each at origin and rescale so longest axis hits the
    // visible pebble size.
    for (const v of chosen) {
      const bb = v.geo.boundingBox!;
      const cx = (bb.min.x + bb.max.x) * 0.5;
      const cz = (bb.min.z + bb.max.z) * 0.5;
      v.geo.translate(-cx, -bb.min.y, -cz);
      const longest = Math.max(
        bb.max.x - bb.min.x,
        bb.max.y - bb.min.y,
        bb.max.z - bb.min.z,
      ) || 1;
      const k = PEBBLE_VISUAL_SIZE / longest;
      v.geo.scale(k, k, k);
      v.geo.computeBoundingBox();
      v.geo.computeVertexNormals();
    }

    // Pre-bucket pebble indices by variant so update() iterates buckets
    // directly — one pass, no per-pebble variant lookup, no
    // per-frame allocation.
    const buckets: number[][] = chosen.map(() => []);
    for (let i = 0; i < this.pebbles.items.length; i++) {
      buckets[i % chosen.length].push(i);
    }

    for (let v = 0; v < chosen.length; v++) {
      const cap = Math.max(1, buckets[v].length);
      // Use the pack's own material directly — keeps the baked textures
      // and natural rock colour the artist authored. No per-world tint.
      const mesh = new THREE.InstancedMesh(chosen[v].geo, chosen[v].mat, cap);
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      mesh.userData.__pebbleMesh = true;
      this.group.add(mesh);
      this.slots.push({ mesh, pebbleIdx: buckets[v] });
    }
    this.loaded = true;
  }

  private fallbackSpheres(): void {
    const geom = new THREE.IcosahedronGeometry(0.04, 1);
    const mat = new THREE.MeshStandardMaterial({ color: 0x6a6660, roughness: 0.95 });
    const idx: number[] = [];
    for (let i = 0; i < this.pebbles.items.length; i++) idx.push(i);
    const mesh = new THREE.InstancedMesh(geom, mat, Math.max(1, idx.length));
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.userData.__pebbleMesh = true;
    this.group.add(mesh);
    this.slots.push({ mesh, pebbleIdx: idx });
    this.loaded = true;
  }

  update(ants: readonly AntState[]): void {
    if (!this.loaded) return;
    const items = this.pebbles.items;
    for (const slot of this.slots) {
      const indices = slot.pebbleIdx;
      for (let k = 0; k < indices.length; k++) {
        const i = indices[k];
        const p = items[i];
        let x: number, z: number, y: number;
        if (p.holder !== -1 && ants[p.holder]?.alive) {
          const a = ants[p.holder];
          const head = 0.08;
          x = a.x + Math.cos(a.heading) * head;
          z = a.z + Math.sin(a.heading) * head;
          y = this.terrain.getHeight(x, z) + 0.04;
        } else {
          if (p.holder !== -1) p.holder = -1;
          x = p.x;
          z = p.z;
          y = this.terrain.getHeight(x, z);
        }
        this.dummy.position.set(x, y, z);
        this.dummy.rotation.set(0, (i * 1.13) % (Math.PI * 2), 0);
        this.dummy.scale.setScalar(1);
        this.dummy.updateMatrix();
        slot.mesh.setMatrixAt(k, this.dummy.matrix);
      }
      slot.mesh.instanceMatrix.needsUpdate = true;
    }
  }

  dispose(): void {
    for (const s of this.slots) {
      s.mesh.geometry.dispose();
      (s.mesh.material as THREE.Material).dispose();
    }
  }
}
